/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';

import MsrpToggle from './msrp-toggle';

interface HeaderUtilityBarProps {
    /**
     * Leading announcement/promo copy. Ships empty — the copy source is still open
     * (CRCB2B-645), and hard-coded placeholder text would later read as real
     * content. The trailing controls stay right-aligned either way.
     */
    promo?: ReactNode;
}

/**
 * SRAM header utility bar — the 40px grey strip above the main row (Figma "Header
 * Banner", Top Navigation node `0:3`): announcement copy left, controls right.
 *
 * Header chrome, so it renders unconditionally in the `full` variant rather than
 * depending on a caller supplying content, and never in `checkout`.
 *
 * No locale control here by design — the storefront's locale switcher lives in the
 * footer. (Figma shows a globe; that glyph is not in the approved subset.)
 */
export default function HeaderUtilityBar({ promo }: HeaderUtilityBarProps): ReactElement {
    return (
        <div className="bg-header-banner-background text-header-banner-foreground">
            {/* h-10 matches Figma's 40px banner. */}
            <div className="section-container flex h-10 items-center justify-between gap-4">
                <div className="min-w-0 flex-1 truncate text-sm">{promo}</div>
                <div className="flex shrink-0 items-center gap-4">
                    <MsrpToggle />
                </div>
            </div>
        </div>
    );
}
